import type { AgentGatewayStatusEvent, ToolStep } from './copilot.types';

export function appendToolStepFromStatus(
  existing: ToolStep[] | undefined,
  evt: AgentGatewayStatusEvent,
): ToolStep[] | undefined {
  const tool = typeof evt.tool === 'string' ? evt.tool.trim() : '';
  if (!tool) {
    return existing;
  }
  const text =
    (typeof evt.label === 'string' && evt.label.trim()) || tool;
  const prev = existing ?? [];
  if (evt.state === 'tool_end') {
    return prev.map((step) =>
      step.tool === tool ? { ...step, done: true } : step,
    );
  }
  if (evt.state !== 'tool_start') {
    return existing;
  }
  if (prev.some((step) => step.tool === tool)) {
    return prev;
  }
  return [...prev, { text, tool, done: false }];
}

export function markToolStepsDone(steps: ToolStep[] | undefined): ToolStep[] | undefined {
  if (!steps?.length) {
    return steps;
  }
  return steps.map((step) => (step.done ? step : { ...step, done: true }));
}

/**
 * Wait this long into a turn before previewing the streamed answer inside the trace. Deliberately
 * short — the preview exists only to fill the wait, so it should appear as soon as there is
 * anything to show. The small delay is not pacing, it just avoids a sub-half-second flash of
 * preview on an answer that lands almost instantly, where the card alone is the better outcome.
 */
export const TRACE_ANSWER_PREVIEW_DELAY_MS = 500;

/** One-line preview budget — fits the panel width at 12.5px without wrapping. */
export const TRACE_ANSWER_PREVIEW_MAX_CHARS = 90;

/**
 * Collapse the answer streamed so far into one short line for the trace. The answer card stays
 * hidden until the turn finishes, so this is the only glimpse of it while a slow answer lands.
 *
 * Runs on every streamed token, so it only ever looks at the head of the answer — enough to fill
 * `max` after markdown is stripped, and no more. Scanning the whole accumulated answer each time
 * would make rendering the trace cost more the longer the answer got, and this feedback must
 * never slow the answer down.
 */
export function answerPreviewLine(answerSoFar: string, max = TRACE_ANSWER_PREVIEW_MAX_CHARS): string {
  const flat = (answerSoFar || '')
    .slice(0, max * 3)
    .replace(/```[\s\S]*?(```|$)/g, ' ')
    .replace(/[*_`>#|]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (flat.length <= max) {
    return flat;
  }
  const cut = flat.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
